import Tooltip from '@mui/material/Tooltip/Tooltip';
import Alert from '@mui/material/Alert/Alert';
import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import { useAuth } from '../../services/useAuth';
import { PageContainer } from '../../components/PageContainer';
import { Loading } from '../../components/Loading';
import { AdminHackathonSummary, archiveHackathon, deleteHackathon, getAdminHackathons, unarchiveHackathon, setHackathonPlan } from '../../services/admin.service';

import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Table from '@mui/material/Table/Table';
import TableHead from '@mui/material/TableHead/TableHead';
import TableBody from '@mui/material/TableBody/TableBody';
import TableRow from '@mui/material/TableRow/TableRow';
import TableCell from '@mui/material/TableCell/TableCell';
import TableContainer from '@mui/material/TableContainer/TableContainer';
import Paper from '@mui/material/Paper/Paper';
import Chip from '@mui/material/Chip/Chip';
import Button from '@mui/material/Button/Button';
import TextField from '@mui/material/TextField/TextField';
import Pagination from '@mui/material/Pagination/Pagination';
import Box from '@mui/material/Box/Box';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogContentText from '@mui/material/DialogContentText/DialogContentText';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import Tabs from '@mui/material/Tabs/Tabs';
import Tab from '@mui/material/Tab/Tab';
import { AdminOrganizations } from './AdminOrganizations';

const PER_PAGE = 20;

export const AdminHomePage = () => {
    const { user, loading } = useAuth();

    const [page, setPage] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState<AdminHackathonSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [loadingRows, setLoadingRows] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<AdminHackathonSummary | null>(null);
    const [planError, setPlanError] = useState('');
    const [tab, setTab] = useState(0);

    const isAdmin = !!user && user.isAdmin;

    const load = useCallback(() => {
        setLoadingRows(true);
        getAdminHackathons(page, search).then(res => {
            setRows(res.hackathons);
            setTotal(res.total);
            setLoadingRows(false);
        }, (err) => {
            console.error(err);
            setLoadingRows(false);
        });
    }, [page, search]);

    useEffect(() => {
        if (isAdmin) {
            load();
        }
    }, [isAdmin, load]);

    const runSearch = () => {
        setPage(0);
        setSearch(searchInput.trim());
    };

    const toggleArchive = (row: AdminHackathonSummary) => {
        setBusyId(row._id);
        const action = row.archived ? unarchiveHackathon : archiveHackathon;
        action(row._id).then(updated => {
            setRows(prev => prev.map(r => (r._id === row._id ? updated : r)));
            setBusyId(null);
        }, (err) => {
            console.error(err);
            setBusyId(null);
        });
    };

    const togglePlan = (row: AdminHackathonSummary) => {
        setBusyId(row._id);
        setPlanError('');
        setHackathonPlan(row._id, row.plan === 'pro' ? 'free' : 'pro').then(updated => {
            setRows(prev => prev.map(r => (r._id === row._id ? { ...r, ...updated } : r)));
            setBusyId(null);
        }, (err) => {
            setPlanError(err?.response?.data?.message || 'Could not change the plan');
            setBusyId(null);
        });
    };

    const doDelete = () => {
        if (!confirmDelete) {
            return;
        }
        const id = confirmDelete._id;
        setBusyId(id);
        deleteHackathon(id).then(() => {
            setConfirmDelete(null);
            setBusyId(null);
            load();
        }, (err) => {
            console.error(err);
            setBusyId(null);
        });
    };

    if (loading) {
        return <PageContainer>
            <Loading />
        </PageContainer>;
    }

    if (!isAdmin) {
        return <PageContainer>
            <h1>Not an admin</h1>
        </PageContainer>;
    }

    const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));

    return (
        <PageContainer>
            <Stack spacing={2}>
                <Typography variant="h1" sx={{ fontSize: '2rem' }}>Administration</Typography>
                <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Hackathons" />
                    <Tab label="Organizations" />
                </Tabs>
                {tab === 1 && <AdminOrganizations />}
                {tab === 0 && <>

                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        size="small"
                        label="Search by name or slug"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { runSearch(); } }}
                    />
                    <Button variant="contained" onClick={runSearch}>Search</Button>
                    {search && (
                        <Button variant="text" onClick={() => { setSearchInput(''); setSearch(''); setPage(0); }}>
                            Clear
                        </Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="body2" color="text.secondary">{total} total</Typography>
                </Stack>

                {planError && <Alert severity="error" onClose={() => setPlanError('')}>{planError}</Alert>}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Created by</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Ideas</TableCell>
                                <TableCell align="right">Participants</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingRows && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress size={24} sx={{ my: 2 }} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loadingRows && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">No hackathons found.</TableCell>
                                </TableRow>
                            )}
                            {!loadingRows && rows.map(row => (
                                <TableRow key={row._id} hover>
                                    <TableCell>
                                        <Link to={`/h/${row.slug}`}>{row.name || row.slug}</Link>
                                    </TableCell>
                                    <TableCell>
                                        {row.createdBy ? (
                                            <Stack>
                                                <span>{row.createdBy.username}</span>
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.createdBy.email}
                                                </Typography>
                                            </Stack>
                                        ) : <span>-</span>}
                                    </TableCell>
                                    <TableCell>
                                        {row.createdAt ? dayjs(row.createdAt).format('MMM D, YYYY') : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            {row.plan === 'pro' && <Chip size="small" color="primary" label={row.planSource === 'granted' ? 'Pro · granted' : row.planSource === 'paid' ? 'Pro · paid' : 'Pro'} />}
                                            {row.archived && <Chip size="small" color="warning" label="Archived" />}
                                            {row.closed && <Chip size="small" label="Closed" />}
                                            {row.hasInviteCode && <Chip size="small" variant="outlined" label="Private" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">{row.ideaCount}</TableCell>
                                    <TableCell align="right">{row.participantCount}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
                                            {/* a paid event stays Pro: refund it in Stripe instead */}
                                            <Tooltip title={row.planSource === 'paid' ? 'Paid for: refund it in Stripe to make it Free' : row.plan === 'pro' ? 'Back to the Free plan' : 'Turn Pro on without a payment'}>
                                                <span>
                                                    <Button
                                                        size="small"
                                                        disabled={busyId === row._id || row.planSource === 'paid'}
                                                        onClick={() => togglePlan(row)}
                                                    >
                                                        {row.plan === 'pro' ? 'Make Free' : 'Make Pro'}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                            <Button
                                                size="small"
                                                disabled={busyId === row._id}
                                                onClick={() => toggleArchive(row)}
                                            >
                                                {row.archived ? 'Unarchive' : 'Archive'}
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                disabled={busyId === row._id}
                                                onClick={() => setConfirmDelete(row)}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {pageCount > 1 && (
                    <Stack direction="row" justifyContent="center">
                        <Pagination
                            count={pageCount}
                            page={page + 1}
                            onChange={(_, value) => setPage(value - 1)}
                        />
                    </Stack>
                )}
                </>}
            </Stack>

            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Delete hackathon?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This permanently deletes <strong>{confirmDelete?.name || confirmDelete?.slug}</strong> along
                        with all of its ideas, votes, and comments. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={!!busyId}
                        onClick={doDelete}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};
